// Per-page help content shown in the HelpModal ("?" button in the topbar).
// Keyed by route, same as PAGE_PROMPTS in Layout.jsx. Written in plain
// language for non-technical business users — avoid "dbt"/"dlt" jargon.

export const PAGE_HELP = {
  '/dashboard': {
    title: 'Dashboard',
    intro: 'Your Dashboard collects the charts and insights you’ve pinned from the AI assistant, organized into sections you control.',
    sections: [
      {
        heading: 'Pinning insights',
        body: 'Ask the AI a question on the Analytics page — when it returns a chart or summary, pin it here so it’s always visible.',
      },
      {
        heading: 'Organizing your layout',
        body: [
          'Click Edit to enter edit mode',
          '+ Section creates a new tab to group related insights',
          'Move → sends a widget to another section',
          '✕ removes a widget from the dashboard (it isn’t deleted from your conversations)',
        ],
      },
      {
        heading: 'Metrics & printing',
        body: 'Metric cards show KPI values calculated from your data — click "Calculate →" if one hasn’t been computed yet. Use Print (or Ctrl+P) to export the current section as a PDF.',
      },
    ],
    suggestedPrompt: 'What are the most important trends I should be tracking?',
  },

  '/analytics': {
    title: 'Analytics',
    intro: 'This is where you talk to the AI about your business data — ask questions in plain English and get answers, charts, and reports back.',
    sections: [
      {
        heading: 'Conversations',
        body: 'Every chat is saved on the left. Start a new one any time with + New, or pick up an old conversation where you left off.',
      },
      {
        heading: 'Asking questions',
        body: 'Ask in plain language — "What were our top 5 customers last quarter?" or "Build me a report on monthly revenue by region." The AI prepares any data it needs automatically.',
      },
      {
        heading: 'Recommendations',
        body: 'The Recommendations tab shows AI-suggested next steps for your data — like filling in missing documentation or building a new report. Click a suggestion to send it straight to chat.',
      },
    ],
    suggestedPrompt: 'What can you tell me about my business based on the data you have access to?',
  },

  '/connectors': {
    title: 'Connectors',
    intro: 'Connect your business systems (QuickBooks, Salesforce, Stripe, and more) so the AI can analyze their data.',
    sections: [
      {
        heading: 'Connecting a new source',
        body: '+ Add Connector opens the catalog — choose your provider and follow the prompts to authorize access.',
      },
      {
        heading: 'Keeping data fresh',
        body: [
          'Sync Now pulls the latest changes immediately',
          'Full Refresh reloads everything from scratch — use this if data looks wrong (it can take longer)',
          'Edit schedule sets how often a connector syncs automatically',
        ],
      },
      {
        heading: 'Data Wizard',
        body: 'After a sync, the Data Wizard automatically reviews and documents the new data so the AI assistant can use it right away — usually nothing else to do.',
      },
    ],
    suggestedPrompt: 'When did the last sync complete for each of my connectors?',
  },

  '/lake': {
    title: 'Catalog',
    intro: 'The Catalog shows every dataset available to the AI, organized into four layers.',
    sections: [
      {
        heading: 'The four layers',
        body: [
          'Raw — exactly what was pulled from your connected systems',
          'Prepared — cleaned, de-duplicated versions the AI builds for analysis',
          'Analytics — business-ready reports and summaries',
          'Reference — lookup files you’ve uploaded (e.g. a CSV of plant locations)',
        ],
      },
      {
        heading: 'Tags & access',
        body: 'Each column can carry a sensitivity tag (high = SSNs/financial IDs, medium = emails/phone numbers) set automatically by the AI, and a classification (Public/Internal/Confidential/Restricted) that you control.',
      },
      {
        heading: 'Promoting & refreshing',
        body: 'Reference files need a description before they can be promoted to production. "Refresh Lake" rebuilds your data models — set a schedule with Edit schedule, or trigger one on demand.',
      },
    ],
    suggestedPrompt: 'What datasets are available for me to build a report from?',
  },

  '/settings': {
    title: 'Settings',
    intro: 'Settings covers your organization’s profile, team, billing, usage, and security configuration.',
    sections: [
      {
        heading: 'Tabs',
        body: [
          'Overview — your organization’s basic info and where your data is stored',
          'Business Profile — context the AI uses to understand your business, plus a setup wizard',
          'Users — invite teammates and manage their access level',
          'Billing — your subscription plan and invoices',
          'Usage — AI usage this month vs. your budget, broken down by team member',
          'Security — encryption, access controls, and data governance settings',
          'Appearance — switch between light and dark mode',
        ],
      },
    ],
    suggestedPrompt: 'Who has access to this workspace?',
  },
}
